import type { ConversationBoardDto } from '../../api/types';
import { readableLinks } from '../point-card-text';

export type HumanAct = NonNullable<ConversationBoardDto['humanActs']>[number];
/** Identidade do ato na tela (DD.17: vários atos podem ter o mesmo tipo). */
export const actKey = (act: HumanAct): string => act.id ?? act.kind;

/**
 * DD.17 (dono, 25/09/2026: "visualmente não percebi nenhuma mudança") — a âncora da aba Apontamentos. Em 3 s o dono
 * sabe se é gargalo: com atos, uma linha por ato (o que fazer, onde, e o botão que abre a tela); sem atos, uma linha
 * dizendo que nada depende dele. A frase do ato é o texto principal; o tipo do ato vira rótulo pequeno.
 */
export function OwnerTurn({ acts, canManage, sending, checkingKind, notice, error, hasOpenItems, ownerPending, onCheck, onOpened }: {
  acts: HumanAct[];
  canManage: boolean;
  sending: boolean;
  checkingKind: string | null;
  notice: { kind: string; text: string } | null;
  error: string;
  hasOpenItems: boolean;
  /** Há apontamento do dono em aberto (mesmo sem a lista de atos, de um servidor antigo): nunca dizer "nada depende". */
  ownerPending: boolean;
  onCheck: (act: HumanAct) => void;
  onOpened: (act: HumanAct) => void;
}) {
  if (!acts.length) {
    return hasOpenItems && !ownerPending
      ? <p className="board-owner-clear" data-testid="board-owner-clear" role="status"><span aria-hidden="true" className="board-owner-clear-dot" />Nada depende de você agora. O Assistente e os terminais seguem sozinhos.</p>
      : null;
  }
  const total = acts.reduce((sum, act) => sum + act.count, 0);
  return <section className="board-owner-turn" data-testid="board-human-acts" aria-label="Sua vez">
    <h3><span className="board-owner-turn-kicker">Sua vez</span> {total === acts.length
      ? `${total} ${total === 1 ? 'coisa só você pode fazer' : 'coisas só você pode fazer'}`
      : `${acts.length === 1 ? '1 ato seu destrava' : `${acts.length} atos seus destravam`} ${total} apontamentos`}</h3>
    <ul>
      {acts.map(act => {
        const main = readableLinks(act.step || act.label);
        return <li key={actKey(act)} className="board-owner-act" data-testid="board-human-act" data-kind={act.kind}>
          {/* O número só diz algo quando um ato destrava vários apontamentos; com um só, um marcador basta. */}
          <span className={act.count > 1 ? 'board-owner-act-count' : 'board-owner-act-count board-owner-act-count--one'} role="img" aria-label={`${act.count} de ${total} pontos dependem deste ato`}>{act.count > 1 ? act.count : null}</span>
          <div className="board-owner-act-body">
            <strong className="board-owner-act-step">{main}</strong>
            <p className="board-owner-act-meta">{[main !== act.label ? act.label : null, act.count > 1 ? `${act.count} pontos` : null, ...act.projects].filter(Boolean).join(' · ')}</p>
            {notice?.kind === actKey(act) ? <p className="board-owner-act-notice" role="status" data-testid="board-human-act-notice">{notice.text}</p> : null}
          </div>
          <div className="board-owner-act-actions">
            {/* DD.3 — a tela exata do ato: um clique leva aonde o dono age, e a conferência vem sozinha 15 min depois. */}
            {act.actionUrl ? <a className="board-owner-act-open" href={act.actionUrl} target="_blank" rel="noopener noreferrer" data-testid="board-human-act-link" onClick={() => onOpened(act)}>Abrir onde fazer ↗</a> : null}
            {canManage ? <button type="button" className="board-owner-act-check" disabled={sending || checkingKind !== null} onClick={() => onCheck(act)}
              title="Depois de fazer, peça a conferência: o terminal de cada projeto confere se mudou e continua o trabalho.">
              {checkingKind === actKey(act) ? 'Conferindo…' : `Já fiz · conferir${act.count > 1 ? ` (${act.count})` : ''}`}
            </button> : null}
          </div>
        </li>;
      })}
    </ul>
    {error ? <p className="objective-error" role="alert">{error}</p> : null}
  </section>;
}
